import { useNodeConnections, useStore } from "@xyflow/react";
import { useMemo } from "react";
import { BlocksType, NodeData } from "./types";

interface UsePortNameProps {
  port?: any;
  type?: BlocksType;
  data: NodeData;
}

export function usePairCheck({ port, type, data }: UsePortNameProps) {
  // Always call hooks at the top level
  const connections = useNodeConnections({
    handleType: "target",
    handleId: port?.id,
  });
  // Get first connection
  const connection = connections?.[0];
  const connectedNodeId = connection?.source;

  const pairBlockAncestor = useStore((state) => {
    const findAncestor = (nodeId: string, visited = new Set<string>()): any => {
      if (visited.has(nodeId)) return null;
      visited.add(nodeId);

      const node = state.nodes.find((n) => n.id === nodeId);
      if (!node || node.data?.type === BlocksType.ACTION) return null;
      if (node.data?.type === BlocksType.PAIR_BLOCK) return node;

      const incomingEdges = state.edges.filter((e) => e.target === nodeId);
      for (const edge of incomingEdges) {
        const ancestor = findAncestor(edge.source, visited);
        if (ancestor) return ancestor;
      }
      return null;
    };

    const startNodeId = connectedNodeId;
    if (!startNodeId) return null;
    return findAncestor(startNodeId);
  });

  return useMemo(() => {
    if (type === BlocksType.PAIR_BLOCK) {
      return {
        isPairBlock: true,
        pairSymbol: undefined,
        pairColor: data?.color,
      };
    }

    if (type === BlocksType.ACTION || !pairBlockAncestor) {
      return {
        isPairBlock: false,
      };
    }

    return {
      isPairBlock: true,
      pairSymbol: pairBlockAncestor.data?.other_inputs?.[0]?.value,
      pairColor: pairBlockAncestor.data?.color,
    };
  }, [type, data?.color, pairBlockAncestor]);
}
